import React, { useEffect, useRef } from 'react'
import { Button } from 'react-bootstrap'
import { useTheme } from 'styled-components'

import authorization from 'live/services/authorization'
import i18n from 'live/services/i18n'

import { Icon } from '../../utils'
import { EditableTitle, TopBarItemStyled, TopBarStyledContainer } from './styles'

interface ITitleProps {
    text: string
    maxWidth: string
    onChange?: (t: string) => void
    maxLength?: number
}

interface ITitlePropsElement {
    element: JSX.Element
    maxWidth: string
    onChange?: (t: string) => void
    maxLength?: number
}

interface IProps {
    title: (ITitleProps | ITitlePropsElement)[]
    handleGoBack: () => void
    handleSave?: () => void
    disabled?: boolean
    saveLoading?: boolean
    backButtonId?: string
    saveButtonId?: string
    hideSaveButton?: boolean
}

export const EditorTopBar: React.FC<IProps> = ({
    handleGoBack,
    handleSave,
    title,
    saveLoading,
    disabled,
    backButtonId,
    saveButtonId,
    hideSaveButton,
    children
}) => {
    const editableTitlesRefs = useRef<Map<string, HTMLInputElement>>(new Map())
    const bowtieAdmin = authorization.superAdmin || authorization.Permission.BOWTIE_ADMIN
    const theme = useTheme()

    useEffect(() => {
        if (editableTitlesRefs.current?.size > 0) {
            for (const [, ref] of editableTitlesRefs.current) {
                ref.style.width = `0px`
                ref.style.width = `${ref.scrollWidth}px`
            }
        }
    }, [])

    return (
        <>
            <TopBarStyledContainer titleItemsCount={title.length}>
                <div className="container">
                    <Button id={backButtonId} className="settings-back" bsSize="small" onClick={handleGoBack}>
                        <Icon type="settings-back" withG={false} color={theme['blue-500']} />
                    </Button>

                    <div className="title-items">
                        {title.map((item, idx, arr) => {
                            return (
                                <>
                                    <TopBarItemStyled
                                        key={`top-bar-item-${idx}`}
                                        className="topbar-item"
                                        percentageWidth={item.maxWidth}
                                    >
                                        {item.onChange && 'text' in item ? (
                                            <>
                                                <EditableTitle
                                                    disabled={!bowtieAdmin}
                                                    id={`input-type-${idx}`}
                                                    className={(item.text ?? '').trim().length === 0 ? 'has-error' : ''}
                                                    defaultValue={item.text}
                                                    ref={ref =>
                                                        editableTitlesRefs.current.set(`input-type-${idx}`, ref)
                                                    }
                                                    onChange={e => {
                                                        const target = e.target as HTMLInputElement

                                                        target.style.width = `0px`
                                                        target.style.width = `${target.scrollWidth}px`

                                                        if ((target.value ?? '').trim().length === 0) {
                                                            target.classList.add('has-error')
                                                        } else {
                                                            target.classList.remove('has-error')
                                                        }
                                                    }}
                                                    onBlur={e => item.onChange((e.target as HTMLInputElement).value)}
                                                    maxLength={item.maxLength}
                                                />

                                                {bowtieAdmin && (
                                                    <label htmlFor={`input-type-${idx}`}>
                                                        {(item.text ?? '').trim().length === 0 ? (
                                                            <i
                                                                className="fas fa-exclamation-circle"
                                                                style={{ color: 'var(--red-500)' }}
                                                            />
                                                        ) : (
                                                            <i
                                                                className="fas fa-edit"
                                                                style={{ color: 'var(--blue-500)' }}
                                                            />
                                                        )}
                                                    </label>
                                                )}
                                            </>
                                        ) : 'element' in item ? (
                                            item.element
                                        ) : (
                                            <div className="text">{item.text}</div>
                                        )}
                                    </TopBarItemStyled>
                                    {idx < arr.length - 1 && (
                                        <div key={`top-bar-separator-${idx}`} className="separator" />
                                    )}
                                </>
                            )
                        })}
                    </div>
                    {children}
                    {!hideSaveButton && bowtieAdmin && (
                        <Button
                            id={saveButtonId}
                            className="save-button"
                            bsStyle="primary"
                            bsSize="small"
                            onClick={handleSave}
                            disabled={disabled || saveLoading}
                        >
                            {saveLoading ? (
                                <>
                                    <i className="fa fa-spinner fa-spin" /> {i18n('Saving')}
                                </>
                            ) : (
                                i18n('Save')
                            )}
                        </Button>
                    )}
                </div>
            </TopBarStyledContainer>
        </>
    )
}
